/**
 * Projects API service
 * Handles communication with the backend for projects
 */

import { apiDelete, apiGet, apiPost, apiPut } from './api';

export interface ProjectResponse {
	id: number;
	offer_id?: number;
	plan_id?: number;
	name: string;
	client: string;
	seeker_name?: string | null;
	seeker_email?: string | null;
	status: 'planning' | 'quoted' | 'in_progress' | 'completed' | 'cancelled';
	budget: number;
	due_date?: string;
	priority: 'low' | 'medium' | 'high';
	tags: string[];
	gantt_data?: any[];
	gardener_ids: number[];
	gardener_names: string[];
	project_company_name?: string | null;
	project_company_id?: number | null;
	created_at: string;
	updated_at: string;
}

export interface CreateProjectPayload {
	offer_id?: number;
	plan_id?: number;
	name: string;
	client: string;
	status?: 'planning' | 'quoted' | 'in_progress' | 'completed' | 'cancelled';
	budget: number;
	due_date?: string;
	priority?: 'low' | 'medium' | 'high';
	tags?: string[];
	gantt_data?: any[];
	gardener_ids?: number[];
}

export interface UpdateProjectPayload extends Partial<CreateProjectPayload> {}

/**
 * AI Re-plan response types
 */
export interface ProjectReplanTimelineChange {
	phase: string;
	description: string;
	affected_tasks: string[];
	delay_days: number;
}

export interface ProjectReplanTaskAdjustment {
	task_reference: string;
	add_days: number;
	reason: string;
}

export interface ProjectReplanNewTask {
	name: string;
	description: string;
	recommended_start_after_task: string;
	estimated_duration_days: number;
	suggested_assignee_role: string;
}

export interface ProjectReplanCostMaterial {
	name: string;
	quantity: number;
	unit: string;
	cost: number;
}

export interface ProjectReplanCostLabor {
	role: string;
	hours: number;
	ratePerHour: number;
	total: number;
}

export interface ProjectReplanCostImpact {
	extra_materials: ProjectReplanCostMaterial[];
	extra_labor_hours: ProjectReplanCostLabor[];
	estimated_extra_cost_min: number;
	estimated_extra_cost_mid: number;
	estimated_extra_cost_max: number;
}

export interface ProjectReplanResult {
	summary: string;
	task_adjustments?: ProjectReplanTaskAdjustment[];
	timeline_changes: ProjectReplanTimelineChange[];
	new_tasks: ProjectReplanNewTask[];
	cost_impact: ProjectReplanCostImpact;
}

export interface ProjectReplanResponse {
	project_id: number;
	focus_task_id?: number | null;
	issue_count: number;
	replan_id: number;
	replan: ProjectReplanResult;
}

/**
 * Get all projects (with optional filters)
 * - user_id: projects where this user is assigned as gardener
 * - seeker_user_id: projects whose plan is owned by this seeker (for "My Projects" tab)
 */
export async function getProjects(params?: {
	user_id?: number;
	seeker_user_id?: number;
	status?: string;
	requester_user_id?: number;
	company_scope?: 'company' | 'all_companies';
}): Promise<ProjectResponse[]> {
	const queryParams = new URLSearchParams();
	if (params?.user_id) queryParams.append('user_id', params.user_id.toString());
	if (params?.seeker_user_id != null) queryParams.append('seeker_user_id', params.seeker_user_id.toString());
	if (params?.status) queryParams.append('status', params.status);
	if (params?.requester_user_id) queryParams.append('requester_user_id', params.requester_user_id.toString());
	if (params?.company_scope) queryParams.append('company_scope', params.company_scope);
	
	const query = queryParams.toString();
	return apiGet<ProjectResponse[]>(`/projects${query ? '?' + query : ''}`);
}

/**
 * Get a specific project by ID
 */
export async function getProjectById(projectId: number): Promise<ProjectResponse> {
	return apiGet<ProjectResponse>(`/projects/${projectId}`);
}

/**
 * Create a new project
 */
export async function createProject(payload: CreateProjectPayload): Promise<ProjectResponse> {
	return apiPost<ProjectResponse>(`/projects`, payload);
}

/**
 * Update a project
 */
export async function updateProject(projectId: number, payload: UpdateProjectPayload): Promise<ProjectResponse> {
	return apiPut<ProjectResponse>(`/projects/${projectId}`, payload);
}

/**
 * Delete a project
 */
export async function deleteProject(projectId: number): Promise<void> {
	return apiPost<void>(`/projects/${projectId}`, {});
}

/**
 * Get gardeners assigned to a project
 */
export async function getProjectGardeners(projectId: number): Promise<any[]> {
	return apiGet<any[]>(`/projects/${projectId}/gardeners`);
}

/**
 * Assign gardener to project
 */
export async function assignGardenerToProject(projectId: number, gardenerUserId: number, role?: string): Promise<any[]> {
	return apiPost<any[]>(`/projects/${projectId}/gardeners`, {
		gardener_user_id: gardenerUserId,
		role
	});
}

/**
 * Remove gardener from project
 */
export async function removeGardenerFromProject(projectId: number, gardenerUserId: number): Promise<void> {
	return apiDelete<void>(`/projects/${projectId}/gardeners/${gardenerUserId}`);
}

/**
 * Request an AI re-plan for a project given logged task issues.
 */
export async function requestProjectReplan(
	projectId: number,
	payload: { task_id?: number; issue_ids?: number[]; user_id?: number; ui_language?: 'en' | 'de' | 'hr' }
): Promise<ProjectReplanResponse> {
	return apiPost<ProjectReplanResponse>(`/projects/${projectId}/ai-replan`, payload);
}

export interface ApplyProjectReplanResponse {
	project_id: number;
	replan_id: number;
	summary: string;
	total_delay_days: number;
	extra_cost_mid: number;
	new_budget: number;
	new_due_date?: string | null;
	created_tasks: Array<{
		id: number;
		title: string;
		start_date: string;
		end_date: string;
		duration: number;
	}>;
}

export async function applyProjectReplan(
	replanId: number,
	payload: { user_id: number; replan: ProjectReplanResult }
): Promise<ApplyProjectReplanResponse> {
	return apiPost<ApplyProjectReplanResponse>(`/project-replans/${replanId}/apply`, payload);
}

export interface ProjectReplanVersion {
	id: number;
	project_id: number;
	focus_task_id: number | null;
	status: 'draft' | 'applied' | 'discarded';
	created_by_user_id: number | null;
	applied_by_user_id: number | null;
	applied_at: string | null;
	applied_task_ids: number[];
	budget_before: number | null;
	due_date_before: string | null;
	created_at: string;
}

export async function listProjectReplans(projectId: number): Promise<ProjectReplanVersion[]> {
	return apiGet<ProjectReplanVersion[]>(`/project-replans?project_id=${projectId}`);
}

export interface RevertProjectReplanResponse {
	project_id: number;
	replan_id: number;
	reverted: boolean;
	deleted_task_ids: number[];
	restored_budget: number | null;
	restored_due_date: string | null;
}

export async function revertProjectReplan(replanId: number, userId: number): Promise<RevertProjectReplanResponse> {
	return apiPost<RevertProjectReplanResponse>(`/project-replans/${replanId}/revert`, { user_id: userId });
}


